// components/strategy/StrategyMfaEnrollForm.jsx — Phase 3B mandatory
// MFA setup: shows the TOTP QR code (qrcode.react, already a project
// dependency) + the raw secret for manual entry, then requires ONE
// valid code to confirm the authenticator app is correctly configured
// before the account becomes usable. Also surfaces the one-time
// recovery codes — shown exactly once, never retrievable again.
import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { strategyEndpoints } from "../../lib/strategyApi";

export const StrategyMfaEnrollForm = ({ mfaSetupToken, onVerified }) => {
  const [provisioningUri, setProvisioningUri] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  React.useEffect(() => {
    let cancelled = false;
    strategyEndpoints.mfaEnroll(mfaSetupToken).then(({ data }) => {
      if (cancelled) return;
      setProvisioningUri(data.provisioning_uri);
      setRecoveryCodes(data.recovery_codes);
    }).catch((e) => setErr(e?.response?.data?.detail || "Impossible de démarrer l'enrôlement MFA."));
    return () => { cancelled = true; };
  }, [mfaSetupToken]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      await strategyEndpoints.mfaVerify(mfaSetupToken, code);
      onVerified();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Code invalide.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!provisioningUri) {
    return <div className="text-xs text-rescue-mute" data-testid="strategy-mfa-loading">Préparation de la configuration MFA…</div>;
  }

  return (
    <div className="border-2 border-rescue-ink bg-white p-4 space-y-3" data-testid="strategy-mfa-enroll-form">
      <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-rescue-mute">
        Scannez avec une application d'authentification (TOTP)
      </div>
      <div className="flex justify-center py-2" data-testid="strategy-mfa-qr">
        <QRCodeSVG value={provisioningUri} size={160} />
      </div>
      <div className="text-[10px] text-rescue-mute break-all font-mono" data-testid="strategy-mfa-secret">{provisioningUri}</div>

      {recoveryCodes && (
        <div className="bg-rescue-bg border-2 border-rescue-line p-3" data-testid="strategy-mfa-recovery-codes">
          <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-rescue-red mb-2">
            Codes de récupération — à conserver, affichés une seule fois
          </div>
          <div className="grid grid-cols-2 gap-1 font-mono text-xs">
            {recoveryCodes.map((c) => <div key={c}>{c}</div>)}
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-3">
        <label className="text-[10px] font-bold tracking-[0.25em] uppercase text-rescue-mute">Code de confirmation</label>
        <input data-testid="strategy-mfa-confirm-code" value={code} onChange={(e) => setCode(e.target.value)}
          placeholder="123456" className="w-full border-2 border-rescue-line px-3 py-2 text-sm font-mono tracking-widest" />
        {err && <div className="text-xs text-rescue-red font-bold" data-testid="strategy-mfa-error">{err}</div>}
        <button type="submit" disabled={submitting} data-testid="strategy-mfa-confirm-submit"
          className="w-full bg-rescue-red text-white py-3 text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-60">
          {submitting ? "Vérification…" : "Confirmer et activer le compte"}
        </button>
      </form>
    </div>
  );
};
